/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Domain Search Hook
 *
 * Reusable hook that abstracts StandardSearch functionality and keyboard shortcuts
 * for domain-specific search implementations. Provides a consistent search pattern
 * across SupplierManager, EquipmentManager, and dashboard hubs.
 */

"use client";

import { useRef } from "react";
import { useKeyboardShortcuts } from "../interactions/use-keyboard-shortcuts";

/**
 * Configuration for the domain search hook
 */
export interface UseDomainSearchConfig {
  /**
   * Placeholder text for the search input
   * @example "Search suppliers..."
   */
  placeholder: string;

  /**
   * Current search value
   */
  searchTerm: string;

  /**
   * Handler when search term changes
   */
  onSearchChange: (value: string) => void;

  /**
   * Optional clear handler
   */
  onClear?: () => void;

  /**
   * Whether user has permission to create new items
   * Used for conditional keyboard shortcuts
   */
  canCreate?: boolean;

  /**
   * Handler for creating new items (triggered by Cmd+N/N shortcuts)
   */
  onCreateNew?: () => void;

  /**
   * Additional keyboard shortcuts specific to the domain
   */
  additionalShortcuts?: Array<{
    keys: string;
    action: () => void;
    preventDefault?: boolean;
  }>;

  /**
   * Optional test ID prefix for the search input
   * Will be used as `${testIdPrefix}-search-input`
   */
  testIdPrefix?: string;
}

/**
 * Return type for useDomainSearch hook
 */
export interface UseDomainSearchReturn {
  /**
   * Ref for the search input (for focusing with keyboard shortcuts)
   */
  searchInputRef: React.RefObject<HTMLInputElement | null>;

  /**
   * Props to spread onto StandardSearch component
   */
  searchProps: {
    placeholder: string;
    value: string;
    onChange: (value: string) => void;
    onClear?: () => void;
    testId?: string;
  };

  /**
   * Helper to focus the search input
   */
  focusSearch: () => void;
}

/**
 * Domain Search Hook
 *
 * Provides standardized search functionality with keyboard shortcuts for domain pages.
 * Abstracts StandardSearch integration and common keyboard shortcuts (mod+f, mod+n, n).
 *
 * Features:
 * - Automatic keyboard shortcuts for search focus (mod+f)
 * - Optional create shortcuts (mod+n, n) when canCreate is enabled
 * - Ref management for search input focus
 * - StandardSearch-compatible props
 * - Extensible with additional domain-specific shortcuts
 *
 * @example
 * ```typescript
 * // Basic usage
 * const { searchInputRef, searchProps, focusSearch } = useDomainSearch({
 *   placeholder: "Search suppliers...",
 *   searchTerm: filters.searchTerm,
 *   onSearchChange: setSearchTerm,
 *   onClear: () => setSearchTerm(""),
 * });
 *
 * return <StandardSearch ref={searchInputRef} {...searchProps} />;
 * ```
 *
 * @example
 * ```typescript
 * // With create functionality
 * const { searchInputRef, searchProps } = useDomainSearch({
 *   placeholder: "Search equipment...",
 *   searchTerm: searchTerm,
 *   onSearchChange: setSearchTerm,
 *   canCreate: canManageData,
 *   onCreateNew: openCreateDialog,
 *   testIdPrefix: "equipment-manager",
 * });
 * ```
 */
export function useDomainSearch(
  config: UseDomainSearchConfig,
): UseDomainSearchReturn {
  const {
    placeholder,
    searchTerm,
    onSearchChange,
    onClear,
    canCreate = false,
    onCreateNew,
    additionalShortcuts = [],
    testIdPrefix,
  } = config;

  const searchInputRef = useRef<HTMLInputElement>(null);

  const focusSearch = () => {
    const input = searchInputRef.current;
    if (input) {
      input.focus();
      input.select();
    }
  };

  // Build keyboard shortcuts array
  const keyboardShortcuts = [
    // Search focus shortcut (mod+f)
    {
      keys: "mod+f",
      action: focusSearch,
      preventDefault: true,
    },
    // Create shortcuts (only if create is enabled)
    ...(canCreate && onCreateNew
      ? [
          {
            keys: "mod+n",
            action: onCreateNew,
            preventDefault: true,
          },
          {
            keys: "n",
            action: onCreateNew,
            preventDefault: false,
          },
        ]
      : []),
    // Additional domain-specific shortcuts
    ...additionalShortcuts,
  ];

  // Register keyboard shortcuts
  useKeyboardShortcuts(keyboardShortcuts);

  // Build StandardSearch props
  const searchProps = {
    placeholder,
    value: searchTerm,
    onChange: onSearchChange,
    onClear,
    testId: testIdPrefix ? `${testIdPrefix}-search-input` : undefined,
  };

  return {
    searchInputRef,
    searchProps,
    focusSearch,
  };
}
